import React from "react";
import { getRibbonBottomBarAriaControlsId, getRibbonBottomBarId, getRibbonBottomBarStylingConstants, menuTextChevronClass, RibbonBottomBar, type ControlInMenuRendererFunction, type ControlRendererFunction, type RibbonBottomBarClassNames, type RibbonModeToggleProps, type RibbonPaddleProps, type RibbonTabDefinitionMLR, type RibbonTabDefinitionSLR, } from "owa-acui";
import { appIcons, getAppStrings } from "owa-acui-theme";
import type { AppPaneUnderlayViewState } from "owa-app-pane-underlay";
import { type CommandingViewMode } from "owa-outlook-service-option-store/lib/store/schema/options/CommandingOptions";
import { FluentIconStyleProvider } from "owa-fluent-icons-svg";
import { onViewModeChanged } from "owa-command-ribbon-store";
import isAppPaneUnderlayExpanded from "owa-app-pane-underlay/lib/utils/isAppPaneUnderlayExpanded";
import { isFlexPaneExpanded } from "owa-suite-header-store";
import { observer } from "owa-mobx-react";
import { useControlInMenuRendererFunction } from "../util/useControlInMenuRendererFunction";
import { isSingleLineRibbon } from "owa-command-ribbon-store/lib/selectors/isSingleLineRibbon";
import ChevronDown from "owa-fluent-icons-svg/lib/icons/ChevronDownRegular";
import { tokens, makeStyles, toggleButtonClassNames, useFocusFinders, } from "@fluentui/react-components";
import { getIsDarkTheme } from "owa-fabric-theme";
import { getIsCommandCenterVisible } from "owa-copilot-command-center-store";
import classnames from "owa-classnames";
import { registerAllLightIcons } from "owa-command-ribbon-light-icon-font";
import { useBlockPlt } from "owa-react-hooks/lib/useBlockPlt";
import { isCapabilityEnabled } from "owa-capabilities";
import { newHeaderCapability } from "owa-capabilities-definitions/lib/newHeaderCapability";
import { isDrawerPaneVisible } from "owa-drawer/lib/selectors/isDrawerPaneVisible";
/* eslint-disable-next-line @typescript-eslint/no-restricted-imports -- (https://aka.ms/OWALintWiki)
 * Remove exception when import is available from @fluentui/react-components Work item 384079
 *	> '@fluentui/react-tabster' import is restricted from being used. Import Fluent v9 components from '@fluentui/react-components' */
import { useTabsterAttributes } from "@fluentui/react-tabster";
import { isGlobalFeatureEnabled } from "owa-feature-flags";
import { RibbonCustomizerContextMenu } from "./RibbonCustomizerContextMenu/RibbonCustomizerContextMenu";
import { useCustomizerContextMenu } from "./RibbonCustomizerContextMenu/useCustomizerContextMenu";
import { singleLineContent } from "./CommandRibbonBottomBar.scss";

// Overwriting styles that `FluentIconStyleProvider` applies
const iconStyles = {
    iconStyle: {
        contain: "inherit",
        height: "auto",
        width: "auto",
    },
    rootStyle: {
        contain: "inherit",
        display: "inherit",
        height: "inherit",
        textAlign: "center",
        verticalAlign: "inherit",
        width: "inherit",
    },
};

const ribbonModeToggleProps: RibbonModeToggleProps = {
    isRibbonModeToggleDisabled: false,
    onRibbonModeToggle: () => {
        // registerAllLightIcons receives a parameter that indicates whether the ribbon is going to be switched to MLR, if so we should load the light icons
        // SLR switching to MLR returns true in isSingleLineRibbon
        // MLR switching to SLR returns false in isSingleLineRibbon
        registerAllLightIcons(isSingleLineRibbon());
        onViewModeChanged(isSingleLineRibbon()
            ? 2 : 1);
    },
    ribbonModeToggleIcon: ChevronDown,
};

const useStyles = makeStyles({
    bottomBar: {
        display: "flex",
    },
    bottomBarContainer: {
        flexGrow: "1",
        minWidth: "0",
        
        // Overwriting `menuTextChevronClass` styles defined in 1JS here:
        // https://office.visualstudio.com/Office/_git/1JS?path=/ooui/packages/acui-ribbon-like/src/UISurfaces/Ribbon/Controls/next/commonRibbonButtonStyles.styles.ts&version=GBmain&line=349&lineEnd=358&lineStartColumn=1&lineEndColumn=1&lineStyle=plain&_a=contents
        [`& .${menuTextChevronClass}`]: {
            paddingLeft: 0,
            width: "auto",
        },
        
        // Fixing High Contrast issue with selected Toggle Buttons
        [`& .${toggleButtonClassNames.icon}`]: {
            forcedColorAdjust: "unset",
        },
    },
    bottomBarContainerGroupDivider: {
        "& .ribbon-bottom-bar-divider": {
            backgroundColor: tokens.colorNeutralStroke1,
        },
    },
    outerContainer: {
        background: "none",
        padding: "0",
    },
});

const getCustomStyles = (outerContainer: string, isFluentV9SLR: boolean): Partial<RibbonBottomBarClassNames> => ({
    outerContainer,
    bottomBarContainer: isFluentV9SLR ? singleLineContent : undefined,
});

export interface CommandRibbonBottomBarProps {
    disableAnimation?: boolean;
    isProjectionPopout: boolean;
    projectionUnderlay?: AppPaneUnderlayViewState;
    selectedTab: RibbonTabDefinitionSLR | RibbonTabDefinitionMLR;
    controlRendererFunction: (c: ControlInMenuRendererFunction) => ControlRendererFunction;
    copilotCommandCenter?: React.ReactNode;
    showRibbonModeToggle?: boolean;
    onCustomizeClick?: () => void;
}

export const bottomBarId = "BottomBar";

export const CommandRibbonBottomBar: React.FunctionComponent<CommandRibbonBottomBarProps> = observer(function CommandRibbonBottomBar({ disableAnimation, isProjectionPopout, projectionUnderlay, selectedTab, controlRendererFunction, copilotCommandCenter, showRibbonModeToggle = true, onCustomizeClick, }: CommandRibbonBottomBarProps) {
    const { controlGroups, id, initialScalingSteps, scalingSteps } = selectedTab;
    const controlInMenuRendererFunction = useControlInMenuRendererFunction(isProjectionPopout);
    const ribbonControlsRef = React.useRef<HTMLDivElement>(null);
    const { findAllFocusable, findFirstFocusable, findLastFocusable } = useFocusFinders();
    
    // This is a temporary solution. Once the 1JS bug is fixed. We should revert this change.
    // TODO: https://outlookweb.visualstudio.com/Outlook%20Web/_workitems/edit/470667
    const onTabKeyDownCapture = React.useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key !== "Tab" ||
            event.ctrlKey ||
            event.altKey ||
            event.metaKey ||
            !ribbonControlsRef.current) {
            return;
        }
        
        const ribbonControls = ribbonControlsRef.current;
        // Avoid importing popoverSurfaceClassNames, which also pulls in the popover styles.
        const focusableElements = findAllFocusable(event.currentTarget).filter(element => !element.closest(".fui-PopoverSurface"));
        const commandElements = focusableElements.filter(element => ribbonControls.contains(element) &&
            !element.matches("[role=\"toolbar\"], [data-focuszone-id]"));
        const copilotElements = focusableElements.filter(element => !ribbonControls.contains(element));
        const firstCopilotElement = copilotElements[0];
        const lastCommandElement = commandElements[commandElements.length - 1];
        const target = event.target;
        const topBar = event.currentTarget
            .closest("#RibbonRoot")
            ?.querySelector<HTMLElement>("#TopBar");
        let nextElement: HTMLElement | null | undefined;
        
        // Hand off focus before the command area's own navigation can loop.
        if (event.shiftKey && target === commandElements[0]) {
            nextElement = topBar ? findLastFocusable(topBar) : undefined;
        }
        else if (!event.shiftKey && target === lastCommandElement) {
            nextElement = firstCopilotElement;
        }
        else if (event.shiftKey && target === firstCopilotElement) {
            nextElement = lastCommandElement;
        }
        else if (!event.shiftKey &&
            target === copilotElements[copilotElements.length - 1] &&
            topBar) {
            nextElement = findFirstFocusable(topBar);
        }
        
        if (nextElement) {
            event.preventDefault();
            event.stopPropagation();
            nextElement.focus();
        }
    }, [findAllFocusable, findFirstFocusable, findLastFocusable]);
    
    const { bottomBar, bottomBarContainer, bottomBarContainerGroupDivider, outerContainer } = useStyles();
    
    const isAppPaneExpanded = isAppPaneUnderlayExpanded(projectionUnderlay) || false;
    const paneStateChanged = isCapabilityEnabled(newHeaderCapability)
        ? isDrawerPaneVisible()
        : isFlexPaneExpanded();
    const isCommandCenterVisible = getIsCommandCenterVisible();
    
    // A bit of a hack since `paddleProps` and `isAppPaneExpanded` are not related, but:
    // 1. We need `RibbonBottombar` to re-render when a pane opens, closes, or expands, or when
    //    the `isCommandCenterVisible` state changes.
    // 2. `RibbonBottombar` will only re-render when its props change.
    //
    // Using `useMemo`, we redefine `paddleProps` only when any of these variables change
    // (resulting in `RibbonBottomBar` re-rendering).
    const paddleProps: RibbonPaddleProps = React.useMemo(() => ({
        disableModeTogglePadding: false,
        ribbonPaddlesIcon: appIcons.downChevron.icon12,
    }), [isAppPaneExpanded, paneStateChanged, isCommandCenterVisible]);
    
    const unblockPlt = useBlockPlt("ribbon");
    
    const containerStyles = classnames(bottomBarContainer, getIsDarkTheme() && bottomBarContainerGroupDivider);
    
    const useTabsterAttribute = useTabsterAttributes({
        focusable: {
            ignoreKeydown: {
                Tab: true,
            },
        },
    });
    
    // Pin the flex container to the ribbon's exact height so the DAB cannot expand it,
    // preventing layout jitter when the DAB toggles visibility.
    const isSLR = isSingleLineRibbon();
    const isFluentV9SLR = isSLR && isGlobalFeatureEnabled("mon-ribbon-fluent-v9-ribbon");
    const bottomBarStyle = React.useMemo(() => ({
        height: getRibbonBottomBarStylingConstants(isSLR ? "SingleLine" : "Multiline")
            .bottomBarHeight,
    }), [isSLR]);
    
    const { contextMenuX, contextMenuY, handleContextMenu, handleDismissContextMenu } = useCustomizerContextMenu();
    
    const enableContextMenu = isGlobalFeatureEnabled("mon-ribbon-mlr-customization") && onCustomizeClick;
    
    const showContextMenu = enableContextMenu && contextMenuX !== null && contextMenuY !== null;
    
    return (<div id={bottomBarId} className={bottomBar} style={bottomBarStyle} onKeyDownCapture={onTabKeyDownCapture}>
                <div {...useTabsterAttribute} ref={ribbonControlsRef} className={containerStyles} onContextMenu={enableContextMenu ? handleContextMenu : undefined} onKeyDown={onKeyDown}>
                    <FluentIconStyleProvider {...iconStyles}>
                        <RibbonBottomBar appStrings={getAppStrings()} ariaControlsId={getRibbonBottomBarAriaControlsId(id)} ariaLabeledBy={id} controlGroups={controlGroups} controlRendererFunction={controlRendererFunction(controlInMenuRendererFunction)} controlInMenuRendererFunction={controlInMenuRendererFunction} customStyles={getCustomStyles(outerContainer, isFluentV9SLR)} disableAnimations={disableAnimation} id={getRibbonBottomBarId(id)} initialScalingSteps={initialScalingSteps} overflowMenuLauncherIcon={appIcons.overflowMenuLauncher} paddleProps={paddleProps} scalingSteps={scalingSteps} preventShadowRendering={true} disableQuickScaling={true} ribbonModeToggleProps={showRibbonModeToggle ? ribbonModeToggleProps : undefined} onAfterScalingCompleted={unblockPlt}/>
                    </FluentIconStyleProvider>
                </div>

                {copilotCommandCenter}

                {showContextMenu && onCustomizeClick && (<RibbonCustomizerContextMenu anchorX={contextMenuX} anchorY={contextMenuY} onCustomizeClick={onCustomizeClick} onDismiss={handleDismissContextMenu}/>)}
            </div>);
}, "CommandRibbonBottomBar");

const onKeyDown: React.KeyboardEventHandler = ({ key, target }) => {
    const isCombobox = target instanceof HTMLElement && target.className.includes("fui-Combobox");
    
    if (key === "ArrowUp" &&
        // When the target is a `Combobox`, `ArrowUp` should have no impact
        // (because `ArrowUp` opens the menu)
        !isCombobox) {
        const selectedTab: HTMLButtonElement | null | undefined = document
            .getElementById("tablist")
            ?.querySelector("button[aria-selected=\"true\"]");
        
        selectedTab?.focus();
    }
};
